import { parseIsoDate } from './dates.js';
import { roundDecimal15 } from './round.js';
/**
 * Excel `TEXT(x, format)` for the formats the workbook uses in its messages.
 *
 * Always `.` for decimals and `,` for thousands, whatever the locale of the browser or server.
 * A blank gives `''`: Excel's TEXT passes an empty text through. A number that is not finite
 * gives `''` as well; it cannot occur in a workbook.
 *
 * A negative number that rounds to zero: a format with one section drops the minus sign
 * (`TEXT(-0.4, "€#,##0")` is `€0`), a format with its own negative section keeps it
 * (`TEXT(-0.4, "€#,##0;-€#,##0")` is `-€0`). This is what the recalculation behind
 * `tools/replay.py` (LibreOffice) prints; Excel itself could not be measured here.
 */
const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
function groupThousands(whole) {
    return whole.replace(/\B(?=(\d{3})+$)/g, ',');
}
function formatNumber(x, format) {
    if (x === null || !Number.isFinite(x))
        return '';
    const { decimals } = format;
    const rounded = roundDecimal15(x, decimals, 'half_away');
    const padded = rounded.scaled.padStart(decimals + 1, '0');
    const whole = padded.slice(0, padded.length - decimals);
    const fraction = padded.slice(padded.length - decimals);
    const wholeText = format.grouped ? groupThousands(whole) : whole;
    const digits = fraction === '' ? wholeText : `${wholeText}.${fraction}`;
    // One section: the sign goes when nothing but zeros is left. Two sections: the negative
    // section is chosen by the number itself, before rounding.
    const roundsToZero = /^0+$/.test(rounded.scaled);
    const minus = rounded.negative && (format.negativeSection || !roundsToZero);
    return `${minus ? '-' : ''}${format.prefix}${digits}${format.suffix}`;
}
/** `#,##0` */
const WHOLE = {
    decimals: 0,
    grouped: true,
    prefix: '',
    suffix: '',
    negativeSection: false,
};
/** `€#,##0` */
const EURO_0 = { ...WHOLE, prefix: '€' };
/** `€#,##0;-€#,##0` */
const EURO_0_SIGNED = { ...EURO_0, negativeSection: true };
/** `€#,##0.00` */
const EURO_2 = { ...EURO_0, decimals: 2 };
/** `0%` */
const PERCENT_0 = { ...WHOLE, grouped: false, suffix: '%' };
/** `0.0%` */
const PERCENT_1 = { ...PERCENT_0, decimals: 1 };
/** `TEXT(x, "€#,##0")`: whole euros, comma thousands. `€12,500`, and `-€441` below zero. */
export function textEuro0(x) {
    return formatNumber(x, EURO_0);
}
/**
 * `TEXT(x, "€#,##0;-€#,##0")`: as `textEuro0`, with the negative form written out. The one
 * difference: a loss of less than half a euro reads `-€0`, not `€0`.
 */
export function textEuro0Signed(x) {
    return formatNumber(x, EURO_0_SIGNED);
}
/** `TEXT(x, "€#,##0.00")`: euros and cents. `€45.00`. */
export function textEuro2(x) {
    return formatNumber(x, EURO_2);
}
/** `TEXT(x, "#,##0")`: a whole number with comma thousands. `1,250`. */
export function textInt(x) {
    return formatNumber(x, WHOLE);
}
/**
 * `TEXT(x, "0%")`: a fraction as a whole percent. `45%`.
 * Multiplies by 100 first and rounds after, in that order, as Excel does.
 */
export function textPct0(x) {
    return formatNumber(x === null ? null : x * 100, PERCENT_0);
}
/**
 * `TEXT(x, "0.0%")`: a fraction as a percent with one decimal. `8.9%`.
 * `0.285 * 100` is `28.499999999999996` in binary; Excel, and this helper, print `28.5%`.
 */
export function textPct1(x) {
    return formatNumber(x === null ? null : x * 100, PERCENT_1);
}
/**
 * `TEXT(date, "d mmm yyyy")` and `TEXT(date, "mmm yyyy")`: the day without a leading zero and
 * the English month, `5 Oct 2026` and `Oct 2026`. A blank or a text that is not a date gives `''`.
 */
export function textDate(date, format) {
    if (date === null)
        return '';
    const civil = parseIsoDate(date);
    if (civil === null)
        return '';
    const month = MONTH_NAMES[civil.month - 1];
    if (month === undefined)
        return '';
    const monthAndYear = `${month} ${civil.year}`;
    return format === 'mmm yyyy' ? monthAndYear : `${civil.day} ${monthAndYear}`;
}
//# sourceMappingURL=text-format.js.map